import { normalizeTags } from '../../utils/tags.js';

function toTagIds(tags, tagIds) {
  if (Array.isArray(tagIds)) {
    return tagIds.map(Number).filter((id) => !Number.isNaN(id));
  }
  return normalizeTags(tags)
    .map((tag) => Number(tag.id))
    .filter((id) => !Number.isNaN(id));
}

export function hydrateOptionTags(option = {}) {
  const tags = normalizeTags(option.tags);
  return {
    ...option,
    tags,
    tag_ids: toTagIds(tags, option.tag_ids),
  };
}

export function hydrateStepTags(step = {}) {
  const tags = normalizeTags(step.tags);
  return {
    ...step,
    tags,
    tag_ids: toTagIds(tags, step.tag_ids),
    options: Array.isArray(step.options)
      ? step.options.map(hydrateOptionTags)
      : [],
  };
}

export function hydrateAutomationTags(automation) {
  if (!automation) return automation;
  return {
    ...automation,
    steps: Array.isArray(automation.steps)
      ? automation.steps.map(hydrateStepTags)
      : [],
  };
}

function serializeOptionTags(option = {}) {
  // eslint-disable-next-line no-unused-vars
  const { tags: _tags, ...rest } = option;
  return {
    ...rest,
    tag_ids: toTagIds(option.tags, option.tag_ids),
  };
}

export function serializeStepTags(step = {}) {
  // eslint-disable-next-line no-unused-vars
  const { tags: _tags, ...rest } = step;
  return {
    ...rest,
    tag_ids: toTagIds(step.tags, step.tag_ids),
    options: Array.isArray(step.options)
      ? step.options.map(serializeOptionTags)
      : [],
  };
}

export function applyRemoteStepTags(localSteps = [], remoteSteps = []) {
  if (!Array.isArray(remoteSteps) || remoteSteps.length === 0) {
    return localSteps;
  }

  const remoteById = new Map();
  const remoteByOrder = new Map();
  remoteSteps.forEach((step) => {
    if (step?.id != null) remoteById.set(String(step.id), step);
    if (step?.order != null) remoteByOrder.set(Number(step.order), step);
  });

  return localSteps.map((local) => {
    // 1. Prioritize matching by step order, which is the unique identity of a step in this flow
    let remote = remoteByOrder.get(Number(local.order));

    // 2. Fallback to ID match ONLY if remote order matches or is not defined
    if (!remote && local.id != null) {
      const candidate = remoteById.get(String(local.id));
      if (
        candidate &&
        (candidate.order == null ||
          Number(candidate.order) === Number(local.order))
      ) {
        remote = candidate;
      }
    }

    if (!remote) return local;

    // Guard: Never cross-contaminate tags if remote explicitly belongs to another step order
    if (
      remote.order != null &&
      local.order != null &&
      Number(remote.order) !== Number(local.order)
    ) {
      return local;
    }

    const remoteHasPopulatedTags =
      (Array.isArray(remote.tags) && remote.tags.length > 0) ||
      (Array.isArray(remote.tag_ids) && remote.tag_ids.length > 0);
    const hydrated = remoteHasPopulatedTags ? hydrateStepTags(remote) : null;

    const finalTags = hydrated ? hydrated.tags : local.tags || [];
    const finalTagIds = hydrated ? hydrated.tag_ids : local.tag_ids || [];

    const updatedOptions = (local.options || []).map((opt) => {
      const remoteOpt = (remote.options || []).find(
        (candidate) =>
          candidate.number === opt.number ||
          (opt.id != null &&
            candidate.id != null &&
            String(candidate.id) === String(opt.id)),
      );
      if (!remoteOpt) return opt;

      const optHasPopulatedTags =
        (Array.isArray(remoteOpt.tags) && remoteOpt.tags.length > 0) ||
        (Array.isArray(remoteOpt.tag_ids) && remoteOpt.tag_ids.length > 0);
      const hydratedOpt = optHasPopulatedTags
        ? hydrateOptionTags(remoteOpt)
        : null;

      return {
        ...opt,
        id: remoteOpt.id ?? opt.id,
        tags: hydratedOpt ? hydratedOpt.tags : opt.tags || [],
        tag_ids: hydratedOpt ? hydratedOpt.tag_ids : opt.tag_ids || [],
      };
    });

    return {
      ...local,
      id: remote.id ?? local.id,
      tags: finalTags,
      tag_ids: finalTagIds,
      options: updatedOptions,
    };
  });
}

export function collectAutomationTags(automation) {
  const seen = new Set();
  const tags = [];

  const push = (list) => {
    normalizeTags(list).forEach((tag) => {
      const key = tag.id != null ? `id:${tag.id}` : `name:${tag.name}`;
      if (seen.has(key)) return;
      seen.add(key);
      tags.push(tag);
    });
  };

  (automation?.steps || []).forEach((step) => {
    push(step.tags);
    (step.options || []).forEach((option) => push(option.tags));
  });

  return tags;
}
